import { useEffect, useRef, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { tokenService } from "../services/tokenService";
import useStore from "./useStore";
import { isMissingRefreshTokenError } from "../utils/auth0Cache";
import {
  clearAppTokensFromStorage,
  safeAuth0Logout,
} from "../utils/authLogout";

const REFRESH_BUFFER_MS = 2 * 60 * 1000;
const MISSING_REFRESH_RECOVERY_KEY = "auth0_missing_refresh_recovery";

/**
 * Hook that orchestrates Auth0 token exchange with the backend.
 * - On first auth, exchanges Auth0 token for a backend JWT + refresh_token
 * - Registers Auth0 fallback in tokenService (used when backend refresh fails)
 * - Schedules proactive token refresh via backend refresh_token before expiry
 */
export const useAuth0TokenExchange = () => {
  const { getAccessTokenSilently, isAuthenticated, logout } = useAuth0();
  const { authStore } = useStore();
  const hasInitialized = useRef(false);
  const refreshTimerRef = useRef(null);

  const handleAuthInitFailure = useCallback(
    async (err) => {
      clearAppTokensFromStorage();
      authStore.clear();

      if (isMissingRefreshTokenError(err)) {
        console.warn(
          "[Auth0] Stale session without refresh token — local logout",
        );
        await safeAuth0Logout(logout, { localOnly: true });
        return;
      }

      console.error("[Auth0] getAccessTokenSilently failed, logging out:", err);
      await safeAuth0Logout(logout, { localOnly: false });
    },
    [authStore, logout],
  );

  const scheduleProactiveRefresh = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    const expiresInMs = tokenService.getTokenExpiresInMs();
    if (expiresInMs === null || expiresInMs <= 0) return;

    const refreshInMs = Math.max(expiresInMs - REFRESH_BUFFER_MS, 0);

    refreshTimerRef.current = setTimeout(async () => {
      try {
        await tokenService.refreshWithBackendToken();
      } catch (err) {
        console.warn("[Auth0] Proactive backend refresh failed, trying Auth0 fallback:", err.message);
        try {
          const auth0Token = await getAccessTokenSilently();
          const tenantUuid = localStorage.getItem("workspace_uuid");
          await tokenService.fetchTokenFromAuth0(auth0Token, tenantUuid);
        } catch (fallbackErr) {
          console.error("[Auth0] Proactive refresh failed entirely:", fallbackErr);
        }
      }
    }, refreshInMs);
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const refreshCallback = async (tenantUuid) => {
      const auth0Token = await getAccessTokenSilently({ cacheMode: "off" });
      return tokenService.fetchTokenFromAuth0(auth0Token, tenantUuid);
    };

    tokenService.setRefreshWithAuth0(refreshCallback);

    return () => {
      tokenService.setRefreshWithAuth0(null);
    };
  }, [getAccessTokenSilently]);

  useEffect(() => {
    const unsubscribe = tokenService.onTokenChange(() => {
      scheduleProactiveRefresh();
    });

    return () => {
      unsubscribe();
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [scheduleProactiveRefresh]);

  useEffect(() => {
    if (!isAuthenticated) {
      hasInitialized.current = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || hasInitialized.current) return;

    const initAuth = async () => {
      try {
        const auth0Token = await getAccessTokenSilently();
        hasInitialized.current = true;
        sessionStorage.removeItem(MISSING_REFRESH_RECOVERY_KEY);
        authStore.getGlobalInformation(auth0Token);
      } catch (err) {
        await handleAuthInitFailure(err);
      }
    };

    initAuth();
  }, [isAuthenticated, getAccessTokenSilently, authStore, handleAuthInitFailure]);
};
